/**
 * /arena/connect -- the consent screen for signing in to the eMerge Skill Arena
 * (emerge-arena docs/DESIGN.md section 3, tranche P5).
 *
 * The Arena sends the browser here with `state` and `code_challenge` (each 43
 * base64url characters). A signed-in person is asked, explicitly, whether the
 * Arena may act with their eMerge access to read PHI for skill runs. Only the
 * click on Continue posts to /api/v1/arena/handoff (P3, backend/src/arena/identity/handoff.js);
 * the server answers with the redirect, and the window follows it ONLY when it
 * begins with ARENA_CALLBACK_PREFIX. No redirect parameter is read from the
 * query: the destination comes from the server alone.
 *
 * Hygiene:
 *  - The query carries state and the challenge, so it is read once and then
 *    stripped from the address bar and history (history.replaceState), and
 *    `<meta name="referrer" content="no-referrer">` is present while the page is open.
 *  - Anything other than exactly the two expected params shows a refusal and never posts.
 *  - Signed out: the portal's own sign-in, returning here with the params kept
 *    (router state `from`, which AuthFlow passes through safeReturnTarget).
 *  - Framing protection: requires the nginx security-header snippet on SPA documents (P6);
 *    gate before ARENA_ENABLED=true. A page cannot enforce this on itself.
 *  - No third-party script is loaded here.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { AUTH_ROUTES } from '../config/authRoutes';

export const ARENA_CALLBACK_PREFIX = 'https://arena.emergesc.com/auth/callback?';
const B64URL_43 = /^[A-Za-z0-9_-]{43}$/;

/** Seam for the final navigation (jsdom cannot navigate). */
export const arenaNav = {
  go: (url: string): void => { window.location.assign(url); },
};

interface Params { state: string; code_challenge: string }

/** Exactly state + code_challenge, once each, both 43 base64url; otherwise null. */
export function parseConnectParams(search: string): Params | null {
  const sp = new URLSearchParams(search);
  const keys = Array.from(sp.keys());
  if (keys.length !== 2 || new Set(keys).size !== 2) return null;
  const state = sp.get('state');
  const challenge = sp.get('code_challenge');
  if (!state || !challenge || !B64URL_43.test(state) || !B64URL_43.test(challenge)) return null;
  return { state, code_challenge: challenge };
}

interface Refusal { code: string; message: string }

const REFUSALS: Record<string, string> = {
  EMERGE_PHI_ACCESS_DENIED: 'Your account is not authorized to access eMerge healthcare data, so it cannot sign in to the Skill Arena.',
  ARENA_IMPERSONATION_REFUSED: 'The Skill Arena cannot be opened from an impersonation or sandbox session. Sign in as yourself and try again.',
  ARENA_DISABLED: 'The Skill Arena is not available right now.',
  ARENA_HANDOFF_INVALID: 'This sign-in request is malformed. Start again from the Skill Arena.',
  ARENA_SESSION_EXPIRED: 'Your portal session has expired. Sign in again, then start again from the Skill Arena.',
  ARENA_NOT_CONFIGURED: 'The Skill Arena is not configured yet.',
  ACCOUNT_NOT_ACTIVE: 'Your eMerge account is not active.',
  ARENA_BAD_REDIRECT: 'The portal received an unexpected destination and stopped. Nothing was sent to the Skill Arena.',
  ARENA_HANDOFF_FAILED: 'The Skill Arena sign-in could not be started. Try again from the Skill Arena.',
};

const refusal = (code: string): Refusal => ({ code, message: REFUSALS[code] || REFUSALS.ARENA_HANDOFF_FAILED });

export function refusalFor(r: { status?: number; code?: string }): Refusal {
  if (r.status === 404) return refusal('ARENA_DISABLED');
  if (r.status === 403 && !r.code) return refusal('ACCOUNT_NOT_ACTIVE');
  if (r.status === 401 || r.code === 'ARENA_HANDOFF_UNAUTHENTICATED') return refusal('ARENA_SESSION_EXPIRED');
  if (r.code && REFUSALS[r.code]) return refusal(r.code);
  if (r.status === 400) return refusal('ARENA_HANDOFF_INVALID');
  return refusal('ARENA_HANDOFF_FAILED');
}

export default function ArenaConnect() {
  const { isAuthenticated, isLoading, user, impersonating } = useUser();
  const location = useLocation();
  // Read once: the query is stripped below, and a re-render must not lose the values.
  const read = useRef<{ params: Params | null } | null>(null);
  if (read.current === null) read.current = { params: parseConnectParams(location.search) };
  const params = read.current.params;
  const inFlight = useRef(false);
  const impersonated = Boolean(impersonating || user?.sandboxTenantId);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Refusal | null>(null);
  const [cancelled, setCancelled] = useState(false);

  // Keep the page out of any Referer; the meta is removed when the page closes.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.parentNode?.removeChild(meta); };
  }, []);

  const signedOutWithParams = !isLoading && !isAuthenticated && params !== null;
  // Strip state + challenge from the address bar and history once read (not while a sign-in redirect still needs them).
  useEffect(() => {
    if (signedOutWithParams) return;
    try { window.history.replaceState(window.history.state, '', location.pathname); } catch { /* history unavailable */ }
  }, [signedOutWithParams, location.pathname]);

  const onContinue = useCallback(async () => {
    if (!params || busy || inFlight.current) return;
    if (impersonated) { setFailure(refusal('ARENA_IMPERSONATION_REFUSED')); return; }
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    const r = await apiRequest<unknown>('POST', '/arena/handoff', { state: params.state, code_challenge: params.code_challenge });
    const redirect = (r as { redirect?: unknown }).redirect;
    if (r.success && typeof redirect === 'string' && redirect.startsWith(ARENA_CALLBACK_PREFIX)) {
      arenaNav.go(redirect);
      return; // stay busy: the window is leaving
    }
    inFlight.current = false;
    setBusy(false);
    setFailure(r.success ? refusal('ARENA_BAD_REDIRECT') : refusalFor(r as { status?: number; code?: string }));
  }, [params, busy, impersonated]);

  if (isLoading) {
    return (
      <Shell><p role="status" className="text-sm text-[#60747b]">Checking your session...</p></Shell>
    );
  }

  if (!params) {
    return <Refused r={refusal('ARENA_HANDOFF_INVALID')} />;
  }
  if (!isAuthenticated) {
    return <Navigate to={AUTH_ROUTES.defaultLogin} state={{ from: { pathname: location.pathname, search: location.search } }} replace />;
  }
  if (cancelled) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold">Sign-in cancelled</h1>
        <p className="mt-2 text-sm leading-6 text-[#60747b]">Nothing was sent to the Skill Arena. You can close this tab.</p>
      </Shell>
    );
  }
  if (failure) return <Refused r={failure} />;
  if (impersonated) return <Refused r={refusal('ARENA_IMPERSONATION_REFUSED')} />;
  const who = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || (user?.id ? `user ${user.id}` : '');
  if (!who) return <Refused r={refusal('ARENA_HANDOFF_FAILED')} />;

  return (
    <Shell>
      <div className="flex items-center gap-2 text-[#10252d]">
        <Glyph d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3zm-1 11l5-5-1.4-1.4L11 11.2 9.4 9.6 8 11l3 3z" />
        <h1 className="text-xl font-semibold">Sign in to the Skill Arena</h1>
      </div>
      <p className="mt-4 text-sm leading-6" data-testid="arena-who">
        You are signed in as <strong>{who}</strong>{who !== user?.email && user?.email ? <> ({user.email})</> : null}.
      </p>
      <p className="mt-3 text-sm leading-6 text-[#60747b]">
        If you continue, the Skill Arena will act with your eMerge access and may read protected health
        information (PHI) to run skills for you. Continue only if you started this from the Skill Arena.
      </p>
      <p className="mt-3 text-sm leading-6 text-[#60747b]" data-testid="arena-terms">
        You will be sent to <strong>arena.emergesc.com</strong>. The Arena session lasts up to 8 hours and
        signs out after 30 minutes idle. Sign out of the Arena or the portal to end it.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onContinue}
          disabled={busy}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#10252d] px-5 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10252d] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          {busy && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
          Continue to Skill Arena
        </button>
        <button
          type="button"
          onClick={() => setCancelled(true)}
          disabled={busy}
          className="inline-flex h-11 items-center justify-center rounded-md border border-[#d7e5e1] bg-white px-5 text-sm font-semibold text-[#10252d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#10252d] focus-visible:ring-offset-2 disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </Shell>
  );
}

function Refused({ r }: { r: Refusal }) {
  return (
    <Shell>
      <div role="alert">
        <div className="flex items-center gap-2 text-[#b94343]">
          <Glyph d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3zm-1 5v5h2V8h-2zm0 7v2h2v-2h-2z" />
          <h1 className="text-xl font-semibold">Skill Arena sign-in refused</h1>
        </div>
        <p className="mt-3 text-sm leading-6 text-[#10252d]">{r.message}</p>
        <p className="mt-2 text-xs text-[#60747b]">Code: <code data-testid="arena-code">{r.code}</code></p>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-[#f6faf9] px-4 py-10 text-[#10252d]">
      <div className="mx-auto w-full max-w-lg rounded-lg border border-[#d7e5e1] bg-white p-6 shadow-sm">{children}</div>
    </main>
  );
}

function Glyph({ d }: { d: string }) {
  return <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6 fill-current"><path d={d} /></svg>;
}
